/**
 * Client-side photo downscaling before upload (Pillar B.1).
 *
 * Crew phones produce 12MP+ photos; the punch list and activity feed need
 * nothing close to that, and the crew are on cellular. Anything bigger than
 * the threshold is redrawn onto a canvas capped at MAX_EDGE px and re-encoded
 * as JPEG. Videos and small images pass through untouched; any failure falls
 * back to the original file (upload must never be blocked by the optimizer).
 */
const MAX_EDGE = 1920;
const JPEG_QUALITY = 0.82;
const SKIP_UNDER_BYTES = 500 * 1024;

export async function downscaleImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.size < SKIP_UNDER_BYTES) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1 && file.type === 'image/jpeg') {
      bitmap.close();
      return file;
    }
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.\w+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
